/** In-place table cell/row updates so a refresh does not remount the panel. */

function cellHtmlForCompare(el: HTMLTableCellElement): string {
  const clone = el.cloneNode(true) as HTMLTableCellElement;
  clone.querySelectorAll(".sfe-advice-dot").forEach((node) => node.remove());
  clone.querySelectorAll(".sfe-advice-dot-host").forEach((node) => {
    node.classList.remove("sfe-advice-dot-host");
  });
  clone.classList.remove("sfe-advice-dot-host");
  return clone.outerHTML;
}

export function morphTableCell(td: HTMLTableCellElement, nextHtml: string): boolean {
  const tpl = document.createElement("template");
  tpl.innerHTML = nextHtml.trim();
  const next = tpl.content.firstElementChild;
  if (!(next instanceof HTMLTableCellElement)) return false;
  if (cellHtmlForCompare(td) === next.outerHTML) return false;

  const adviceDot = td.querySelector(":scope .sfe-advice-dot");
  for (const attr of [...td.attributes]) {
    if (!next.hasAttribute(attr.name)) td.removeAttribute(attr.name);
  }
  for (const attr of next.attributes) {
    td.setAttribute(attr.name, attr.value);
  }
  td.innerHTML = next.innerHTML;
  if (adviceDot instanceof HTMLElement) {
    const host =
      td.querySelector<HTMLElement>(".sfe-options-ticker-cell, .tk-cell, [data-col='ticker'], [data-col='underlying_ticker']") ??
      td;
    host.classList.add("sfe-advice-dot-host");
    if (!host.querySelector(":scope > .sfe-advice-dot")) host.prepend(adviceDot);
  }
  return true;
}

export function rowFromHtml(html: string): HTMLTableRowElement | null {
  const tpl = document.createElement("template");
  tpl.innerHTML = html.trim();
  const row = tpl.content.firstElementChild;
  return row instanceof HTMLTableRowElement ? row : null;
}

/** Reuse existing <tr> nodes by key; move/add/remove without replacing the tbody. */
export function reconcileTableBodyByKey<T>(
  tbody: HTMLElement,
  rows: T[],
  opts: {
    rowSelector: string;
    rowKey: (row: T) => string;
    existingKey: (tr: HTMLTableRowElement) => string;
    emptyHtml: string;
    createRow: (row: T, index: number) => HTMLTableRowElement | null;
    patchRow: (tr: HTMLTableRowElement, row: T, index: number) => void;
  },
): void {
  if (!rows.length) {
    const onlyRow = tbody.querySelector("tr");
    if (onlyRow?.querySelector("td.muted") && !opts.existingKey(onlyRow)) return;
    tbody.innerHTML = opts.emptyHtml;
    return;
  }

  const existingRows = [...tbody.querySelectorAll<HTMLTableRowElement>(opts.rowSelector)];
  const pool = new Map<string, HTMLTableRowElement[]>();
  for (const tr of existingRows) {
    const key = opts.existingKey(tr);
    if (!key) {
      tr.remove();
      continue;
    }
    const list = pool.get(key) ?? [];
    list.push(tr);
    pool.set(key, list);
  }

  const used = new Set<HTMLTableRowElement>();
  let cursor: ChildNode | null = tbody.firstChild;

  rows.forEach((row, index) => {
    const key = opts.rowKey(row);
    const list = key ? pool.get(key) : undefined;
    let tr = list?.shift() ?? null;
    if (tr) {
      opts.patchRow(tr, row, index);
    } else {
      tr = opts.createRow(row, index);
      if (!tr) return;
    }
    used.add(tr);
    if (tr !== cursor) tbody.insertBefore(tr, cursor);
    cursor = tr.nextSibling;
  });

  for (const tr of existingRows) {
    if (!used.has(tr)) tr.remove();
  }
  tbody.querySelectorAll("tr").forEach((tr) => {
    if (tr instanceof HTMLTableRowElement && tr.querySelector("td.muted") && !opts.existingKey(tr)) {
      tr.remove();
    }
  });
}
